// CLIENT PORTAL — the email-link handshake's one piece of local state.
//
// Firebase's signInWithEmailLink needs the address the link was requested for,
// as a defence against a link being intercepted and completed by someone else.
// The browser that requested it remembers; a DIFFERENT browser (the classic
// case: requested on a laptop, opened on a phone) legitimately will not, so the
// finish page has to be able to ask. Neither path is an error, and treating the
// missing value as one would strand exactly the user who did nothing wrong.

const STORAGE_KEY = 'kyck-portal-email';

/**
 * localStorage is unavailable in some privacy modes and throws rather than
 * returning null, so every access is guarded. Failing to remember the address
 * degrades to "ask for it on the finish page" — which is the same path a
 * cross-device sign-in already takes, so there is nothing extra to build.
 */
export function storePortalEmail(email: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, email.trim().toLowerCase());
  } catch {
    /* the finish page asks instead */
  }
}

export function readStoredPortalEmail(): string | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value && value.trim() ? value.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

export function clearStoredPortalEmail(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clean up */
  }
}

/**
 * Turn a Firebase sign-in failure into something a client can act on.
 *
 * ⚠️ Firebase's own messages are written for developers ("Firebase: Error
 * (auth/invalid-action-code)"). A client seeing that concludes the portal is
 * broken; the truth is almost always that they used a link twice or waited too
 * long, and the fix — request another — is one they can perform themselves.
 */
export function describeSignInError(code: string | null | undefined): string {
  switch (code) {
    case 'auth/invalid-action-code':
    case 'auth/expired-action-code':
      return 'That sign-in link has expired or has already been used. Request a new one below.';
    case 'auth/invalid-email':
      return "That doesn't look like the email address this link was sent to.";
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact your account manager.';
    default:
      return "We couldn't sign you in with that link. Request a new one below.";
  }
}
